import { useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { apiRequest } from '@/lib/queryClient';
import { Loader2, CheckCircle2, DollarSign, CreditCard } from 'lucide-react';
import EmbeddedCheckout from '@/components/EmbeddedCheckout';
import { useToast } from '@/hooks/use-toast';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function TestCheckoutFlow() {
  const [checkoutId, setCheckoutId] = useState('');
  const [clientId, setClientId] = useState('9cOlp4k1AacSLI2n');
  const [mode, setMode] = useState<'takeout' | 'delivery'>('takeout');
  const [tipAmount, setTipAmount] = useState('0');
  
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [feeCalculation, setFeeCalculation] = useState<any>(null);
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const { toast } = useToast();

  const handleCreateTestCheckout = async () => {
    if (!clientId) {
      toast({
        title: "Missing Client ID",
        description: "Please enter a Client ID first",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiRequest('POST', '/api/stripe/create-test-checkout', {
        clientId,
        mode
      });
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setCheckoutId(data.checkoutId);
      toast({
        title: "Checkout Created",
        description: `Created ${data.orderType || 'ASAP'} checkout ${data.checkoutId} with item: ${data.itemUsed}${data.orderTime ? ` for ${data.orderTime}` : ''}`,
      });
    } catch (error: any) {
      console.error('Create checkout error:', error);
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create test checkout",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateFees = async () => {
    if (!checkoutId || !clientId) {
      toast({
        title: "Missing Information",
        description: "Please enter both Checkout ID and Client ID",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiRequest('POST', '/api/stripe/calculate-fees', {
        checkoutId,
        clientId,
        tipAmount: parseFloat(tipAmount)
      });
      
      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'Fee calculation failed');
      }
      
      setFeeCalculation(data);
      setStep(2);
      toast({
        title: "Fees Calculated",
        description: "Successfully retrieved fee structure from Eatzy",
      });
    } catch (error: any) {
      console.error('Fee calculation error:', error);
      toast({
        title: "Calculation Failed",
        description: error.message || "Failed to calculate fees",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePaymentIntent = async () => {
    if (!feeCalculation) return;

    try {
      setIsLoading(true);
      
      const restaurantStripeAccountId = feeCalculation.restaurant?.stripe_account_id;
      
      if (!restaurantStripeAccountId) {
        throw new Error("No connected Stripe account found for this restaurant");
      }

      const response = await apiRequest('POST', '/api/stripe/payment-intent', {
        checkoutId,
        clientId,
        restaurantStripeAccountId,
        feeCalculation: feeCalculation.calculation,
        metadata: {
          test_flow: 'true',
          source: 'kudoz-app-test-page'
        }
      });
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setPaymentIntent(data);
      setStep(3);
      toast({
        title: "Payment Intent Created",
        description: "Ready to process payment with Stripe",
      });
    } catch (error: any) {
      console.error('Payment intent error:', error);
      toast({
        title: "Setup Failed",
        description: error.message || "Failed to create payment intent",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    toast({
      title: "Test Complete",
      description: "Payment flow completed successfully!",
    });
    setStep(4);
  };

  return (
    <div className="container mx-auto py-8 max-w-2xl">
      <h1 className="text-3xl font-bold mb-6">Stripe Embedded Checkout Test</h1>
      
      <div className="space-y-6">
        {/* Step 1: Input & Calculate Fees */}
        <Card className={step === 1 ? 'border-primary' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-sm">1</span>
              Checkout Configuration
            </CardTitle>
            <CardDescription>Enter the Eatzy Checkout ID and Client ID to start</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkoutId">Checkout ID</Label>
                  <Input 
                    id="checkoutId" 
                    placeholder="e.g. C123456" 
                    value={checkoutId} 
                    onChange={(e) => setCheckoutId(e.target.value)}
                    disabled={step > 1}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientId">Client ID</Label>
                  <div className="flex gap-2">
                    <Input 
                      id="clientId" 
                      placeholder="e.g. ovfrvkakjcuaozou" 
                      value={clientId} 
                      onChange={(e) => setClientId(e.target.value)}
                      disabled={step > 1}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mode">Order Mode</Label>
                  <Select value={mode} onValueChange={(v: 'takeout' | 'delivery') => setMode(v)} disabled={step > 1}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="takeout">Takeout</SelectItem>
                      <SelectItem value="delivery">Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                   {step === 1 && (
                      <Button 
                        variant="outline" 
                        onClick={handleCreateTestCheckout}
                        disabled={isLoading || !clientId}
                        title="Create a real checkout for testing"
                        className="w-full"
                      >
                        Auto-Create Test Checkout
                      </Button>
                    )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tip">Tip Amount ($)</Label>
                <Input 
                  id="tip" 
                  type="number" 
                  min="0" 
                  step="0.01" 
                  value={tipAmount} 
                  onChange={(e) => setTipAmount(e.target.value)}
                  disabled={step > 1}
                />
              </div>
              
              {step === 1 && (
                <Button onClick={handleCalculateFees} disabled={isLoading} className="w-full">
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
                  Calculate Fees
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Fee Summary & Payment Intent */}
        {step >= 2 && feeCalculation && (
          <Card className={step === 2 ? 'border-primary' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-sm">2</span>
                Fee Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-md bg-muted p-4 text-sm">
                  <div className="flex justify-between mb-2">
                    <span>Restaurant:</span>
                    <span className="font-medium">{feeCalculation.restaurant.name || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Connected Account:</span>
                    <span className="font-mono text-xs">{feeCalculation.restaurant.stripe_account_id || 'Missing!'}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${feeCalculation.calculation.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sales Tax:</span>
                      <span>${feeCalculation.calculation.sales_tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Fee:</span>
                      <span>${feeCalculation.calculation.delivery_fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Service Fee:</span>
                      <span>${feeCalculation.calculation.service_fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Driver Tip:</span>
                      <span>${feeCalculation.calculation.driver_tip.toFixed(2)}</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>${feeCalculation.calculation.total.toFixed(2)}</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="text-xs text-muted-foreground mt-2">
                      <p>Platform Fee (Eatzy): ${feeCalculation.calculation.platform_fee.toFixed(2)}</p>
                      <p>Restaurant Net: ${feeCalculation.calculation.restaurant_amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {step === 2 && (
                  <Button onClick={handleCreatePaymentIntent} disabled={isLoading} className="w-full">
                    {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                    Create Payment Intent
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Payment */}
        {step >= 3 && paymentIntent && (
          <Card className={step === 3 ? 'border-primary' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-sm">3</span>
                Payment
              </CardTitle>
              <CardDescription>Enter card details to complete order</CardDescription>
            </CardHeader>
            <CardContent>
              {step === 3 ? (
                <div className="p-4 border rounded-md">
                  <EmbeddedCheckout
                    onPaymentSuccess={(_id: string) => handlePaymentSuccess()}
                    onCancel={() => console.log('Cancelled')}
                    clientSecret={paymentIntent.clientSecret}
                    stripePublishableKey={paymentIntent.publishableKey || ''}
                    stripeAccountId={feeCalculation?.restaurant?.stripe_account_id || ''}
                    amount={paymentIntent.amount}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center p-8 text-green-600 bg-green-50 rounded-md">
                  <CheckCircle2 className="h-8 w-8 mr-2" />
                  <span className="font-bold text-lg">Payment Confirmed!</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
